import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, filter, of, switchMap } from 'rxjs';
import { LoaderComponent } from 'src/app/components/loader/loader.component';
import { PictureComponent } from 'src/app/components/picture/picture.component';
import { Category } from '../../../../models/category';
import { CompanyActivity } from '../../../../models/company';
import { CreateOfferData } from '../../../../models/create-offer/create-offer-data';
import { AuthService } from '../../../../services/auth.service';
import { CategoryService } from '../../../../services/category.service';
import { CompanyService } from '../../../../services/company.service';
import { CategoryUtils } from '../../../../utils/category.utils';

@Component({
    selector: 'app-pick-category',
    templateUrl: './pick-category.component.html',
    styleUrls: ['./pick-category.component.scss'],
    imports: [CommonModule, TranslateModule, RouterModule, LoaderComponent, PictureComponent],
})
export class PickCategoryComponent implements OnInit {
    @Output() nextStep = new EventEmitter<CreateOfferData>();
    @Input() data: CreateOfferData;
    @Input() readonly = false;
    @Input() isEdit = false;

    protected categoryLoading = true;
    protected currentCategories: Category[] = [];
    protected navigationStack: Category[] = [];
    protected selectedPath: Category[] = [];

    private activity: CompanyActivity | null = null;
    private personal = true;

    constructor(
        private readonly authService: AuthService,
        private readonly categoryService: CategoryService,
        private readonly companyService: CompanyService,
    ) {}

    ngOnInit() {
        this.authService.userLoggedIn
            .pipe(
                filter((user) => !!user),
                switchMap((user) => {
                    this.personal = !(user.pro || user.type === 'team');
                    if (user.company && (user.pro || user.type === 'team')) {
                        return this.companyService.get(user.company).pipe(catchError(() => of(null)));
                    }
                    return of(null);
                }),
            )
            .subscribe({
                next: (company) => {
                    this.activity = company?.activity ?? null;
                    this.categoryService.categories.pipe(filter((categories) => !!categories)).subscribe({
                        next: () => {
                            this.restoreFromData();
                            this.categoryLoading = false;
                        },
                    });
                },
            });
    }

    get selectedCategory(): Category | null {
        return this.data?.categoryData?.category ?? null;
    }

    get selectedCategoryFullName(): string {
        return this.selectedCategory ? CategoryUtils.getFullName(this.selectedCategory) : '';
    }

    confirmReadonly(): void {
        this.nextStep.emit(this.data);
    }

    /**
     * Restore drill-down state from data.categoryData.category (when user comes back to this step).
     * For a non-leaf selection, rebuild navigationStack/selectedPath; for a leaf selection, open its parent
     * level so the user sees the leaf in the grid.
     */
    private restoreFromData(): void {
        const selected = this.selectedCategory;
        if (!selected) {
            this.loadRootCategories();
            return;
        }

        const ancestors: Category[] = [];
        let current: Category | null = selected;
        while (current) {
            ancestors.unshift(current);
            current = current.parent ? CategoryUtils.getById(current.parent) : null;
        }

        const openUpTo = this.hasVisibleChildren(selected) ? ancestors : ancestors.slice(0, -1);

        if (openUpTo.length === 0) {
            this.loadRootCategories();
            return;
        }

        this.navigationStack = [...openUpTo];
        this.selectedPath = [...openUpTo];
        const deepest = openUpTo[openUpTo.length - 1];
        this.currentCategories = this.filterCategories(deepest.children ?? []);
    }

    private loadRootCategories(): void {
        const allCategories = CategoryUtils.getRootCategories();
        this.currentCategories = this.filterCategories(allCategories);
    }

    navigateToCategory(category: Category): void {
        const visibleChildren = this.filterCategories(category.children ?? []);
        if (visibleChildren.length > 0) {
            this.navigationStack.push(category);
            this.selectedPath.push(category);
            this.currentCategories = visibleChildren;
            return;
        }

        if (this.isSelectable(category)) {
            this.selectCategory(category);
        }
    }

    navigateBack(): void {
        if (this.navigationStack.length > 0) {
            this.navigationStack.pop();
            this.selectedPath.pop();

            if (this.navigationStack.length === 0) {
                this.loadRootCategories();
            } else {
                const parent = this.navigationStack[this.navigationStack.length - 1];
                this.currentCategories = this.filterCategories(parent.children ?? []);
            }
        }
    }

    navigateToRoot(): void {
        this.navigationStack = [];
        this.selectedPath = [];
        this.loadRootCategories();
    }

    navigateToLevel(targetIndex: number): void {
        this.navigationStack = this.navigationStack.slice(0, targetIndex + 1);
        this.selectedPath = this.selectedPath.slice(0, targetIndex + 1);

        const targetCategory = this.navigationStack[targetIndex];
        this.currentCategories = this.filterCategories(targetCategory.children ?? []);
    }

    private selectCategory(category: Category): void {
        this.data.categoryData.category = category;
        this.nextStep.emit(this.data);
    }

    hasChildren(category: Category): boolean {
        return this.hasVisibleChildren(category);
    }

    private hasVisibleChildren(category: Category): boolean {
        return this.filterCategories(category.children ?? []).length > 0;
    }

    private filterCategories(categories: Category[]): Category[] {
        return categories
            .filter(
                (category) =>
                    category.isEnabled && category.isPlatform(this.data.platformData.platform) && this.isVisibleForActivity(category),
            )
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    private isVisibleForActivity(category: Category): boolean {
        const platformChildren = (category.children ?? []).filter(
            (child) => child.isEnabled && child.isPlatform(this.data.platformData.platform),
        );

        if (platformChildren.length === 0) {
            return this.isSelectable(category);
        }

        return platformChildren.some((child) => this.isVisibleForActivity(child));
    }

    private isSelectable(category: Category): boolean {
        const platform = this.data.platformData.platform;
        if (!platform) {
            return false;
        }

        return CategoryUtils.isPublicationAllowed(category, platform, this.activity, this.personal);
    }
}
